import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSeo } from "@/lib/useSeo";
import { ArrowRight, Lightning, Truck, ShieldCheck } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1594028355881-9942abbaa2af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxuZXclMjB6ZWFsYW5kJTIwbGFuZHNjYXBlJTIwc3Vuc2V0fGVufDB8fHx8MTc4MTE0MzY2OHww&ixlib=rb-4.1.0&q=85";
const CAT_IMGS = {
  outdoors: "https://images.unsplash.com/photo-1485809052957-5113b0ff51af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHx0b29scyUyMGVsZWN0cm9uaWNzJTIwY2FtcGluZyUyMGdlYXJ8ZW58MHx8fHwxNzgxMTQzNjY4fDA&ixlib=rb-4.1.0&q=85",
  tools: "https://images.unsplash.com/photo-1546827209-a218e99fdbe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHx0b29scyUyMGVsZWN0cm9uaWNzJTIwY2FtcGluZyUyMGdlYXJ8ZW58MHx8fHwxNzgxMTQzNjY4fDA&ixlib=rb-4.1.0&q=85",
  electronics: "https://images.unsplash.com/photo-1615655406736-b37c4fabf923?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxjb25zdW1lciUyMGVsZWN0cm9uaWNzJTIwZ2FkZ2V0c3xlbnwwfHx8fDE3ODExNDM2Njh8MA&ixlib=rb-4.1.0&q=85",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ product_count: 0 });

  useSeo({
    title: "Stuffly — NZ Bargain Marketplace",
    description: "Shop 4,900+ curated New Zealand imports. Phones, tools, electronics, camping and outdoor gear. Fast Auckland dispatch and secure Stripe checkout.",
    path: "/",
    image: "https://images.unsplash.com/photo-1594028355881-9942abbaa2af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  });

  useEffect(() => {
    api.get("/products?limit=8&sort=price-desc").then(r => setFeatured(r.data.items || []));
    api.get("/stats").then(r => setStats(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="grid grid-cols-1 md:grid-cols-2 border-b border-zinc-900">
        <div className="p-8 md:p-14 lg:p-20 flex flex-col justify-between gap-12">
          <div>
            <div className="tag mb-6" data-testid="hero-tag">NZ Marketplace · 2026</div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.92] font-[Outfit]">
              Bargain<br />
              <span className="text-[#ff3333]">Imports</span><br />
              Made Honest.
            </h1>
            <p className="mt-8 text-lg leading-relaxed max-w-md text-zinc-700">
              {stats.product_count.toLocaleString()}+ curated products dispatched from Auckland. <strong>Honest pricing</strong>. No mystery fees. No fake sales.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="hero-shop-btn" to="/products" className="btn-brutal btn-red">
              Shop The Catalogue <ArrowRight size={16} weight="bold" className="ml-2" />
            </Link>
            <Link data-testid="hero-register-btn" to="/register" className="btn-outline">Create Account</Link>
          </div>
        </div>
        <div className="relative noise min-h-[320px] md:min-h-[600px] border-t md:border-t-0 md:border-l border-zinc-900 overflow-hidden">
          <img src={HERO_IMG} alt="NZ Landscape" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute bottom-6 left-6 right-6 bg-white border border-zinc-900 p-4 flex justify-between items-center gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold font-[Outfit]">Live Catalogue</div>
              <div className="text-3xl font-black font-[Outfit] tracking-tighter">{stats.product_count}+</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest font-bold font-[Outfit]">Dispatched</div>
              <div className="text-3xl font-black font-[Outfit] tracking-tighter text-[#ff3333]">24h</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="grid grid-cols-1 md:grid-cols-3 grid-borders border-l border-zinc-900 border-b">
        {[
          { Icon: Lightning, t: "Fast NZ Dispatch", d: "Orders dispatched within 24h from Auckland." },
          { Icon: Truck, t: "Free Over $100", d: "Flat $9.90 shipping, free above $100 NZD." },
          { Icon: ShieldCheck, t: "Stripe Secured", d: "All transactions encrypted by Stripe." },
        ].map(({ Icon, t, d }) => (
          <div key={t} className="p-8 flex items-start gap-4">
            <Icon size={36} weight="duotone" />
            <div>
              <div className="font-[Outfit] font-bold uppercase tracking-tight">{t}</div>
              <p className="text-sm text-zinc-600 mt-1">{d}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Categories */}
      <section className="border-b border-zinc-900">
        <div className="px-6 md:px-12 py-8 border-b border-zinc-900 flex justify-between items-end">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase font-[Outfit]">Browse The Aisles</h2>
          <Link to="/products" className="text-xs uppercase tracking-widest font-bold font-[Outfit] hover:underline">All →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 grid-borders border-l border-zinc-900">
          {[
            { name: "Outdoors", cat: "Camping & Outdoors", img: CAT_IMGS.outdoors },
            { name: "Tools", cat: "Tools & DIY", img: CAT_IMGS.tools },
            { name: "Electronics", cat: "Tech & Gadgets", img: CAT_IMGS.electronics },
          ].map(c => (
            <Link key={c.name} to={`/products?category=${encodeURIComponent(c.cat)}`} className="relative aspect-[4/3] overflow-hidden group" data-testid={`category-${c.name.toLowerCase()}`}>
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-center text-white">
                <span className="text-3xl font-black uppercase tracking-tighter font-[Outfit]">{c.name}</span>
                <ArrowRight size={28} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section>
          <div className="px-6 md:px-12 py-8 border-b border-zinc-900 flex justify-between items-end">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase font-[Outfit]">Top Picks</h2>
            <Link to="/products" className="text-xs uppercase tracking-widest font-bold font-[Outfit] hover:underline">View All →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-borders border-l border-zinc-900 border-b">
            {featured.map(p => <ProductCard key={p.id} p={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
